import { useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';
import { Box, CircularProgress, Grid, TextField, Button, Typography, Divider } from '@mui/material';
import { Formik } from 'formik';
import * as Yup from 'yup';

import FormDialog from '../../../components/FormDialog';
import { useLanguage } from '../../../utils/i18n';
import { formatUpperCase } from '../../../utils/formatTitleCase';

// Import komponen builder dari vehicle-type
import { AxleBuilder, AxleLivePreview } from '../vehicle-type/AxleBuilder';

import { getAxleTypeById, createAxleType, updateAxleType } from '../../../stores/axleTypeStore';

const AxleBuilderAddEdit = forwardRef(
  ({ open, id, onClose, onSuccess, onError, onSubmitStart = () => {}, onSubmitEnd = () => {} }, ref) => {
    const { t } = useLanguage();
    const formikRef = useRef();
    const [loading, setLoading] = useState(false);

    const [initialValues, setInitialValues] = useState({
      name: '',
      description: '',
      axles: []
    });

    useImperativeHandle(ref, () => ({
      submit: () => {
        if (formikRef.current) {
          formikRef.current.submitForm();
        }
      }
    }));

    useEffect(() => {
      const fetchData = async () => {
        if (!id || !open) return;
        setLoading(true);
        try {
          const data = await getAxleTypeById(id);
          if (data) {
            let parsedAxles = [];
            let configObj = data.axle_configuration;

            // if axle_configuration is a string (JSON)
            if (typeof configObj === 'string') {
              try {
                configObj = JSON.parse(configObj);
              } catch (e) {
                console.error('Failed to parse axle_configuration', e);
              }
            }

            if (configObj && Array.isArray(configObj.info)) {
              parsedAxles = configObj.info;
            } else if (Array.isArray(data.axles)) {
              // fallback just in case backend returns axles directly
              parsedAxles = data.axles;
            }

            setInitialValues({
              name: data.name || '',
              description: data.description ?? '',
              axles: parsedAxles
            });
          }
        } catch (err) {
          console.error(err);
          onError && onError(t('notify.failedLoadData', 'Failed to load data'));
        } finally {
          setLoading(false);
        }
      };

      if (open) {
        if (!id) {
          const defaultValues = {
            name: '',
            description: '',
            axles: []
          };
          setInitialValues(defaultValues);
          setTimeout(() => {
            if (formikRef.current) {
              formikRef.current.resetForm({ values: defaultValues });
            }
          }, 0);
        }
        fetchData();
      }
    }, [id, open, onError, t]);

    const validationSchema = Yup.object().shape({
      name: Yup.string().required(t('validation.required', 'Required')),
      description: Yup.string().nullable(),
      axles: Yup.array().min(1, 'At least one axle is required').required()
    });

    const handleSubmit = async (values, { setSubmitting }) => {
      setSubmitting(true);
      onSubmitStart();
      try {
        const payload = {
          name: formatUpperCase(values.name) ?? '',
          description: values.description ?? '',
          axle_configuration: {
            info: (values.axles || []).map((axle, index) => {
              const axleNumber = index + 1;
              const isFront = axleNumber === 1;
              const rearNumber = axleNumber - 1;

              const totalAxles = (values.axles || []).length;
              const rStr = totalAxles > 2 ? String(rearNumber) : '';
              const rLabel = totalAxles > 2 ? ` ${rearNumber}` : '';
              const positions = [];

              if (isFront) {
                if (axle.is_twin_tire) {
                  positions.push({ code: 'FL-OUT', label: 'FRONT LEFT - OUT', order: `${axleNumber}.1` });
                  positions.push({ code: 'FL-IN', label: 'FRONT LEFT - IN', order: `${axleNumber}.2` });
                  positions.push({ code: 'FR-IN', label: 'FRONT RIGHT - IN', order: `${axleNumber}.3` });
                  positions.push({ code: 'FR-OUT', label: 'FRONT RIGHT - OUT', order: `${axleNumber}.4` });
                } else {
                  positions.push({ code: 'FL', label: 'FRONT LEFT', order: `${axleNumber}.1` });
                  positions.push({ code: 'FR', label: 'FRONT RIGHT', order: `${axleNumber}.2` });
                }
              } else {
                if (axle.is_twin_tire) {
                  positions.push({ code: `R${rStr}L-OUT`, label: `REAR${rLabel} LEFT - OUT`, order: `${axleNumber}.1` });
                  positions.push({ code: `R${rStr}L-IN`, label: `REAR${rLabel} LEFT - IN`, order: `${axleNumber}.2` });
                  positions.push({ code: `R${rStr}R-IN`, label: `REAR${rLabel} RIGHT - IN`, order: `${axleNumber}.3` });
                  positions.push({ code: `R${rStr}R-OUT`, label: `REAR${rLabel} RIGHT - OUT`, order: `${axleNumber}.4` });
                } else {
                  positions.push({ code: `R${rStr}L`, label: `REAR${rLabel} LEFT`, order: `${axleNumber}.1` });
                  positions.push({ code: `R${rStr}R`, label: `REAR${rLabel} RIGHT`, order: `${axleNumber}.2` });
                }
              }

              return {
                ...axle,
                axle_number: axleNumber,
                positions
              };
            })
          }
        };

        // Ambil company dari localStorage untuk disematkan di payload
        try {
          const storedCompany = typeof window !== 'undefined' ? localStorage.getItem('companyId') : null;
          if (storedCompany) {
            payload.company = Number(storedCompany);
          } else {
            const rawUser = typeof window !== 'undefined' ? localStorage.getItem('user') : null;
            if (rawUser) {
              const u = JSON.parse(rawUser);
              if (u?.company || u?.company_id) {
                payload.company = Number(u.company || u.company_id);
              }
            }
          }
        } catch (e) {
          console.error('Error parsing company for payload', e);
        }

        let response;
        if (id) {
          response = await updateAxleType(id, payload);
          onSuccess && onSuccess(t('notify.update', 'Updated successfully'));
        } else {
          response = await createAxleType(payload);
          onSuccess && onSuccess(t('notify.create', 'Created successfully'));
        }

        if (response?.code === 400 || response?.status === false || response?.error) {
          throw response;
        }
      } catch (err) {
        console.error('Submit error:', err);
        const errorMessage = err?.response?.data?.message || err?.data?.message || err?.message || 'Gagal menyimpan data';
        onError && onError(errorMessage);
      } finally {
        setSubmitting(false);
        onSubmitEnd();
      }
    };

    return (
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue, isSubmitting }) => (
          <FormDialog
            open={open}
            onClose={onClose}
            title={id ? t('axleBuilder.edit', 'Edit Axle Layout') : t('axleBuilder.add', 'Add Axle Layout')}
            fullScreenMode
            fullHeight={false}
            actions={
              <>
                <Button
                  variant="contained"
                  type="submit"
                  form="axle-builder-form"
                  disabled={isSubmitting || loading}
                  sx={{ backgroundColor: '#0072DA', textTransform: 'none' }}
                  startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
                >
                  {isSubmitting ? t('buttonTitle.processing', 'Processing...') : t('buttonTitle.save', 'Save')}
                </Button>
                <Button onClick={onClose} disableRipple disableElevation variant="outlined" sx={{ textTransform: 'none' }}>
                  {t('buttonTitle.cancel', 'Cancel')}
                </Button>
              </>
            }
          >
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4, minHeight: 200, alignItems: 'center' }}>
                <CircularProgress />
              </Box>
            ) : (
              <form id="axle-builder-form" onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item size={{ xs: 12, md: 6 }}>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 700, color: 'primary.main' }}>
                      Axle Information
                    </Typography>

                    <TextField
                      fullWidth
                      size="medium"
                      id="name"
                      name="name"
                      label={t('axleBuilder.name', 'Layout Name')}
                      value={values.name}
                      onChange={(e) => setFieldValue('name', formatUpperCase(e.target.value))}
                      onBlur={handleBlur}
                      error={touched.name && !!errors.name}
                      helperText={touched.name && errors.name}
                      required
                      InputLabelProps={{ sx: { '& .MuiFormLabel-asterisk': { color: 'error.main' } } }}
                      sx={{ mb: 2 }}
                    />

                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      id="description"
                      name="description"
                      label={t('general.description', 'Description')}
                      value={values.description ?? ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.description && !!errors.description}
                      helperText={touched.description && errors.description}
                      sx={{ mb: 2 }}
                    />

                    <Divider sx={{ my: 3, borderStyle: 'dashed', borderColor: '#cbd5e1' }} />
                    <AxleBuilder axles={values.axles} setFieldValue={setFieldValue} />

                    {errors.axles && typeof errors.axles === 'string' && (
                      <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                        {errors.axles}
                      </Typography>
                    )}
                  </Grid>

                  <Grid item size={{ xs: 12, md: 6 }}>
                    <AxleLivePreview axles={values.axles || []} />
                  </Grid>
                </Grid>
              </form>
            )}
          </FormDialog>
        )}
      </Formik>
    );
  }
);

export default AxleBuilderAddEdit;
